/** SPEC §16.2: Beheer › Instellingen › Audit-log – wie heeft wat wanneer gedaan. */
import { Fragment, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { auditOmschrijving } from '@ep/core';
import { api } from '../lib/api';
import { useData, useIk } from '../lib/data';
import { Icoon } from '../ui/Icoon';
import { Laden } from '../ui/ui';

interface Regel {
  id: string;
  userEmail: string | null;
  actie: string;
  details: { onderwerp?: string; wijzigingen?: Record<string, { van: unknown; naar: unknown }>; namens?: string; [k: string]: unknown } | null;
  ip: string | null;
  tijd: string;
}
interface Antwoord {
  regels: Regel[];
  totaal: number;
  pagina: number;
  perPagina: number;
  personen: string[];
  acties: string[];
  bewaarMaanden: number;
}

const LEEG = { van: '', tot: '', wie: '', actie: '', q: '' };

/** camelCase-veldnaam → leesbaar ("urenPerDag" → "uren per dag"). */
const veldNaam = (k: string) =>
  k
    .split('.')
    .map((d) => d.replace(/Id$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase())
    .join(' › ');

/** Details één niveau plat, bijv. invoer.datum (de invoer zelf zonder voorvoegsel). */
function plat(o: Record<string, unknown>): [string, unknown][] {
  const uit: [string, unknown][] = [];
  for (const [k, v] of Object.entries(o)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) for (const [s, x] of Object.entries(v)) uit.push([k === 'invoer' ? s : `${k}.${s}`, x]);
    else uit.push([k, v]);
  }
  return uit;
}

export function AuditLog() {
  const { data: d } = useData();
  const ik = useIk().data;
  const [f, setF] = useState(LEEG);
  const [zoek, setZoek] = useState('');
  const [pagina, setPagina] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
    return p;
  }, [f]);
  const q = useQuery({
    queryKey: ['audit', qs.toString(), pagina],
    queryFn: () => api.get<Antwoord>(`/api/beheer/audit?${qs.toString()}&pagina=${pagina}`),
    placeholderData: keepPreviousData,
  });
  const zet = (x: Partial<typeof LEEG>) => {
    setF({ ...f, ...x });
    setPagina(1);
  };

  // id's in wijzigingen tonen als naam
  const namen = useMemo(() => {
    const m = new Map<string, string>();
    if (!d) return m;
    d.engineers.forEach((e) => m.set(e.id, e.naam));
    d.projecten.forEach((p) => m.set(p.id, `${p.nummer} ${p.naam}`));
    d.werkpakketten.forEach((w) => m.set(w.id, w.naam));
    d.gebruikers.forEach((u) => m.set(u.id, u.naam));
    return m;
  }, [d]);
  const waarde = (v: unknown): string => {
    if (v == null || v === '') return '–';
    if (typeof v === 'boolean') return v ? 'ja' : 'nee';
    if (typeof v === 'string') return namen.get(v) ?? v;
    if (Array.isArray(v)) return v.map(waarde).join(', ') || '–';
    return JSON.stringify(v);
  };

  const a = q.data;
  const paginas = a ? Math.max(1, Math.ceil(a.totaal / a.perPagina)) : 1;
  const tijd = (t: string) => new Date(t).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'medium' });
  const acties = useMemo(() => (a?.acties ?? []).map((x) => ({ x, l: auditOmschrijving(x) })).sort((p, r) => p.l.localeCompare(r.l, 'nl')), [a?.acties]);

  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1200 }}>
      <div className="paneel kolom" style={{ gap: 12 }}>
        <div className="rij wrap">
          <div className="groei">
            <h1>Audit-log</h1>
            <div className="klein mute">
              Wie heeft wat wanneer gedaan in {ik?.organisatie.naam ?? 'deze organisatie'}: inloggen, aanmaken, wijzigen (oud → nieuw) en verwijderen. Regels worden {a?.bewaarMaanden ?? 12} maanden bewaard.
            </div>
          </div>
          <a className="knop" href={`/api/beheer/audit.csv?${qs.toString()}`} download>
            <Icoon naam="download" maat={16} /> Exporteren (CSV)
          </a>
        </div>
        <div className="rij wrap audit-filters">
          <label className="klein">Van <input type="date" value={f.van} onChange={(e) => zet({ van: e.target.value })} style={{ width: 'auto' }} /></label>
          <label className="klein">t/m <input type="date" value={f.tot} onChange={(e) => zet({ tot: e.target.value })} style={{ width: 'auto' }} /></label>
          <select value={f.wie} onChange={(e) => zet({ wie: e.target.value })} style={{ width: 'auto' }} aria-label="Persoon">
            <option value="">Iedereen</option>
            {(a?.personen ?? []).map((p) => <option key={p} value={p}>{p.startsWith('platform:') ? `${p.slice(9)} (platformbeheer)` : p}</option>)}
          </select>
          <select value={f.actie} onChange={(e) => zet({ actie: e.target.value })} style={{ width: 'auto' }} aria-label="Soort actie">
            <option value="">Alle acties</option>
            {acties.map(({ x, l }) => <option key={x} value={x}>{l}</option>)}
          </select>
          <form className="rij" onSubmit={(e) => { e.preventDefault(); zet({ q: zoek.trim() }); }}>
            <input type="search" placeholder="Zoeken…" value={zoek} onChange={(e) => setZoek(e.target.value)} onBlur={() => zoek.trim() !== f.q && zet({ q: zoek.trim() })} style={{ width: 200 }} />
          </form>
          {(f.van || f.tot || f.wie || f.actie || f.q) && (
            <button className="knop klein" onClick={() => { setF(LEEG); setZoek(''); setPagina(1); }}>Filters wissen</button>
          )}
        </div>
        {!a ? <Laden /> : (
          <>
            <div className="tabel-scroll">
              <table className="tabel audit-tabel">
                <thead><tr><th style={{ width: 150 }}>Tijd</th><th>Wie</th><th>Actie</th><th>Onderwerp</th><th style={{ width: 28 }} /></tr></thead>
                <tbody>
                  {a.regels.map((r) => {
                    const w = r.details?.wijzigingen;
                    const { onderwerp, wijzigingen, namens, ...rest } = r.details ?? {};
                    void wijzigingen;
                    // id en titel zijn al zichtbaar als onderwerp
                    if (onderwerp) { delete rest.id; delete rest.titel; }
                    const gegevens = plat(rest);
                    const meer = !!w || gegevens.length > 0 || !!r.ip;
                    const isOpen = open === r.id;
                    const platform = r.userEmail?.startsWith('platform:');
                    return (
                      <Fragment key={r.id}>
                        <tr className={meer ? 'klik' : undefined} onClick={() => meer && setOpen(isOpen ? null : r.id)}>
                          <td className="klein nowrap">{tijd(r.tijd)}</td>
                          <td className="klein">
                            {platform ? <><span className="chip">platformbeheer</span> {r.userEmail!.slice(9)}</> : r.userEmail}
                            {namens ? <div className="mute">namens {String(namens)}</div> : null}
                          </td>
                          <td>
                            {auditOmschrijving(r.actie)}{w ? <span className="klein mute"> · {Object.keys(w).length} veld{Object.keys(w).length === 1 ? '' : 'en'}</span> : null}
                            <div className="audit-mob klein mute">{onderwerp ? `${String(onderwerp)} · ` : ''}{platform ? `platformbeheer ${r.userEmail!.slice(9)}` : r.userEmail}</div>
                          </td>
                          <td>{onderwerp ? String(onderwerp) : <span className="mute">–</span>}</td>
                          <td className="mute">{meer && <Icoon naam="chevron-down" maat={16} style={{ transform: isOpen ? 'rotate(180deg)' : undefined }} />}</td>
                        </tr>
                        {isOpen && (
                          <tr className="audit-details">
                            <td colSpan={5}>
                              {w && (
                                <table className="audit-wijzigingen">
                                  <thead><tr><th>Veld</th><th>Was</th><th>Wordt</th></tr></thead>
                                  <tbody>
                                    {Object.entries(w).map(([k, x]) => (
                                      <tr key={k}><td>{veldNaam(k)}</td><td className="was">{waarde(x.van)}</td><td className="wordt">{waarde(x.naar)}</td></tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {gegevens.length > 0 && (
                                <table className="audit-wijzigingen">
                                  <thead><tr><th>Gegeven</th><th colSpan={2}>Waarde</th></tr></thead>
                                  <tbody>{gegevens.map(([k, v]) => <tr key={k}><td>{veldNaam(k)}</td><td colSpan={2}>{waarde(v)}</td></tr>)}</tbody>
                                </table>
                              )}
                              <div className="klein mute">Actie <code>{r.actie}</code>{r.ip ? <> · IP-adres {r.ip}</> : null}</div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!a.regels.length && <tr><td colSpan={5} className="leeg">Geen regels gevonden.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="rij wrap klein">
              <span className="groei mute">{a.totaal.toLocaleString('nl-NL')} regel{a.totaal === 1 ? '' : 's'}{q.isFetching ? ' · laden…' : ''}</span>
              <button className="knop klein" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Vorige</button>
              <span>Pagina {pagina} van {paginas}</span>
              <button className="knop klein" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>Volgende</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
